import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

const sizeClass = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  ml: 'max-w-xl',
  lg: 'max-w-2xl',
  // `xl` reserved for dialogs that embed wider tables (e.g. the
  // Work Report detail view's 7-column Work Details table). The
  // Modal's existing `max-h-[90vh]` + body scroll handle tall
  // content, so this only changes the horizontal cap.
  xl: 'max-w-4xl',
}

/**
 * Generic Modal.
 *
 * Dismissal model:
 *   - **Escape key** always closes (deliberate keystroke, not an accident).
 *   - **X button + Cancel/footer buttons** always close.
 *   - **Backdrop click** does NOT close by default — most modals in this
 *     app carry form data, and a stray click outside shouldn't discard
 *     what the user typed. Opt-in via `dismissOnBackdrop` for purely
 *     read-only previews (e.g. image lightbox, info-only popups) where
 *     the user expects the click-outside affordance.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  // Optional exact-width override (e.g. `max-w-[550px]`). When set it wins
  // over the named `size` so a caller can pin a precise width without adding
  // a one-off entry to `sizeClass`. Still `w-full` underneath, so it stays
  // responsive and shrinks on narrow viewports.
  widthClass,
  dismissOnBackdrop = false,
  // Overlay stacking. Defaults to `z-50` — the app's modal layer. Raise it
  // (e.g. `z-[90]`) when a modal is opened from ON TOP of another portaled
  // layer that already sits above z-50, such as the comments popover
  // (zIndex 80), so the modal isn't painted behind it.
  overlayZClass = 'z-50',
}) {
  const dialogRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') return onClose()
      if (e.key !== 'Tab') return
      // Keep Tab inside the dialog. `aria-modal="true"` already tells assistive
      // technology that the rest of the page is inert; without a matching
      // keyboard trap that promise is false for anyone tabbing.
      const dlg = dialogRef.current
      if (!dlg) return
      const focusable = [...dlg.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((el) => el.offsetParent !== null)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && (document.activeElement === first || !dlg.contains(document.activeElement))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  useEffect(() => {
    if (!open) return undefined
    /**
     * Move focus INTO the dialog, and put it back where it came from on close.
     *
     * Without this, opening a modal left focus on `<body>`: a keyboard user
     * pressing Tab walked into the page BEHIND the modal, and a screen-reader
     * user was told (by `aria-modal`) that the rest of the page was inert
     * while their focus was still sitting in it (QA-100).
     *
     * The first field is preferred over the container so the common case —
     * open a form, start typing — works without an extra Tab. A frame is
     * allowed to pass first so the content has mounted.
     */
    const previouslyFocused = document.activeElement
    const id = requestAnimationFrame(() => {
      const dlg = dialogRef.current
      if (!dlg) return
      const target =
        dlg.querySelector('input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled])') ??
        dlg.querySelector('button:not([disabled]), a[href]') ??
        dlg
      target.focus?.()
    })
    return () => {
      cancelAnimationFrame(id)
      // Only restore if focus is still inside the dialog (or nowhere) — if the
      // user has deliberately clicked elsewhere, leave them there.
      const active = document.activeElement
      const inside = dialogRef.current?.contains(active)
      if ((inside || active === document.body) && previouslyFocused?.isConnected) {
        previouslyFocused.focus?.()
      }
    }
  }, [open])

  if (!open) return null

  // Portal to body so the modal escapes any ancestor with `transform` /
  // `filter` / `perspective` (those create a containing block for fixed
  // children, which would otherwise pin the modal inside the sidebar).
  return createPortal(
    <div
      // Marks the whole modal layer (dialog + backdrop). A popover that
      // dismisses on outside-click can check `closest('[data-modal-overlay]')`
      // to avoid closing itself (and unmounting this modal) when the user
      // interacts with a confirm opened from inside it — see TaskComments.
      data-modal-overlay=""
      className={`fixed inset-0 ${overlayZClass} flex items-center justify-center bg-black/30 backdrop-blur-[1.8px] p-0 sm:p-4`}
      onMouseDown={(e) => {
        // Backdrop dismissal is opt-in. Most callers are forms — a
        // stray click outside shouldn't discard the user's typed data.
        if (!dismissOnBackdrop) return
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        // Focusable as a fallback target when the dialog holds no field or
        // button of its own; -1 keeps it out of the normal tab ring.
        tabIndex={-1}
        // max-h + flex-column so the body can scroll while the header
        // and footer stay visible. Without this, tall content (e.g.
        // the workspace template picker) pushes the action buttons
        // below the viewport with no way to reach them.
        // Mobile height cap is `max-h-full` (100% of the fixed inset-0 overlay
        // = the viewport) rather than `100dvh`: it needs no `dvh` unit support,
        // so the body still scrolls on older mobile browsers where `dvh` is
        // ignored (which otherwise let the box grow unbounded → no scroll).
        className={`w-full ${widthClass ?? sizeClass[size]} max-h-full sm:max-h-[90vh] flex flex-col rounded-none sm:rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)] overflow-hidden`}
      >
        <div className="shrink-0 flex items-start justify-between gap-4 border-b border-[color:var(--color-rule)] px-5 py-4">
          <div className="min-w-0 flex-1">
            {/* Modal title — `text-lg font-semibold`. Previously
             *  `text-xl font-semibold tracking-wide` which competed
             *  visually with the page H1 (text-3xl) and added
             *  unnecessary letter-spacing. The new size is the canonical
             *  "sub-page header" tier used across the app.
             *  `break-words`/`overflow-wrap:anywhere` mirrors the
             *  ConfirmDialog message so a long, space-less value baked
             *  into the title (e.g. a filename like
             *  "Donation_Certificate_Priya_Mehta.pdf" in a Delete prompt)
             *  wraps inside the header instead of overflowing and being
             *  clipped by the dialog's `overflow-hidden`. `min-w-0` on the
             *  wrapper lets this flex child shrink so the break can take
             *  effect; the close button stays put via `shrink-0`. */}
            <h2
              id="modal-title"
              className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 break-words [overflow-wrap:anywhere]"
            >
              {title}
            </h2>
            {description ? (
              // Description bumped from text-xs (12px) to text-sm (14px)
              // so it actually reads as supporting copy rather than a
              // caption — captions are reserved for meta lines below.
              // `break-words`/`overflow-wrap:anywhere` mirrors the title above
              // so a long, space-less value interpolated into the description
              // (e.g. a pasted company name "ttgfgvbvvg…") wraps inside the
              // header instead of overflowing and being clipped by the dialog's
              // `overflow-hidden`.
              <p className="mt-1 text-sm text-[color:var(--color-ink-muted)] break-words [overflow-wrap:anywhere]">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            title="Close"
            // 24x24 minimum hit area (WCAG 2.5.8). Sized in px, not rem: the
            // app's Text-size control scales the root font size (13px on
            // Compact), so a rem-based minimum would fall under 24 CSS px.
            className="inline-flex min-h-[24px] min-w-[24px] shrink-0 items-center justify-center p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {children}
        </div>
        {footer ? (
          <div className="shrink-0 flex justify-end gap-2 border-t border-[color:var(--color-rule)] px-5 py-3 bg-[color:var(--color-canvas)]">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
